import { FC, useEffect, useRef, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Print } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { AddRackDrawer } from "../../../../components/admin/add-racks-drawer/AddRackDrawer";
import {
  getallRacksAction,
  deleteRackAction,
} from "../../../../redux/actions/rackAction";
import { PrintableRackLabels } from "./BarcodePDF";

type Rack = {
  id: number;
  rackId?: string;
  rackLocation: string;
  rackCapacity?: number;
  rackDescription?: string;
  conveyorName?: string;
  convayorName?: string;
  slots?: { slotId: number; slotName: string }[];
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: { fontWeight: "bold" },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

export const Racks: FC = () => {
  const dispatch = useAppDispatch();
  const { getAllRacks, getAllRacksLoading, addRacks } = useAppSelector(
    (state) => state.rack
  );

  const snackBar = useSnackbarContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRackId, setSelectedRackId] = useState<number | undefined>();
  const [openDrawer, setOpenDrawer] = useState(false);

  const [barcodeList, setBarcodeList] = useState<string[]>([]);
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
  });

  useEffect(() => {
    dispatch(getallRacksAction());
  }, [dispatch]);

  useEffect(() => {
    if (addRacks) dispatch(getallRacksAction());
  }, [addRacks, dispatch]);

  const racks: Rack[] = ((getAllRacks as any)?.body ??
    (getAllRacks as any)?.data ??
    []) as Rack[];

  const handleClick = (event: MouseEvent<HTMLButtonElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedRackId(id);
  };
  const handleClose = () => setAnchorEl(null);
  const handleOnDrawerClose = () => setOpenDrawer(false);

  const selectedRack = selectedRackId
    ? racks.find((r) => r.id === selectedRackId)
    : undefined;

  useEffect(() => {
    if (showPrintDialog && barcodeList.length > 0) {
      setTimeout(() => {
        handlePrint();
        setShowPrintDialog(false);
      }, 100);
    }
  }, [showPrintDialog, barcodeList, handlePrint]);

  const handlePrintRack = () => {
    if (!selectedRack) {
      snackBar.showSnackbar("No Rack selected!", "error");
      return;
    }

    const baseName = (
      selectedRack.id ??
      selectedRack.id ??
      selectedRack.rackLocation ??
      ""
    )
      .toString()
      .replace(/\s+/g, "")
      .toLowerCase();

    const capacity = selectedRack.slots?.length;
    if (!capacity || typeof capacity !== "number") {
      snackBar.showSnackbar("Invalid rack capacity!", "error");
      return;
    }

    const barcodes = Array.from(
      { length: capacity },
      (_, i) => `${baseName}-${String(i + 1).padStart(2, "0")}`
    );

    setBarcodeList(barcodes);
    setShowPrintDialog(true);
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <AddRackDrawer
          open={openDrawer}
          toggleDrawer={handleOnDrawerClose}
          populateFor={
            selectedRack
              ? {
                  id: selectedRack.id,
                  rackName: selectedRack.convayorName,
                  rackId: selectedRack.rackId || "",
                  rackCapacity: selectedRack.slots?.length,
                }
              : undefined
          }
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Racks
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            disabled={getAllRacksLoading}
            onClick={() => {
              setOpenDrawer(true);
              setSelectedRackId(undefined);
            }}
          >
            {getAllRacksLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add New"
            )}
          </CustomButton>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: "72vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="rack table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Rack ID</StyledTableCell>
                <StyledTableCell>Rack Name</StyledTableCell>
                <StyledTableCell>Slots</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllRacksLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!getAllRacksLoading && racks.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={4}
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    No racks found
                  </StyledTableCell>
                </StyledTableRow>
              )}

              {!getAllRacksLoading &&
                racks.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell sx={{ minWidth: 60, maxWidth: "15w" }}>
                      {row.rackId ?? row.id}
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 50, maxWidth: "15vw" }}>
                      {row.convayorName}
                    </StyledTableCell>

                    <StyledTableCell sx={{ minWidth: 250, maxWidth: "30vw" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                          p: 0.5,
                          cursor: "pointer",
                          "&::-webkit-scrollbar": { height: 0 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#ADADAD",
                            borderRadius: 4,
                          },
                        }}
                      >
                        {row.slots?.length ? (
                          row.slots.map((s) => (
                            <Box
                              key={s.slotId}
                              sx={{
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "999px",
                                bgcolor: Colors.BACKSHADE_GRAY,
                                display: "inline-flex",
                                alignItems: "center",
                                fontSize: 12,
                                fontWeight: 600,
                                color: Colors.TEXT_GRAY_DARK,
                              }}
                            >
                              {s.slotName}
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            no slots
                          </Typography>
                        )}
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell>
                      <IconButton onClick={(e) => handleClick(e, row.id)}>
                        <MoreHorizIcon sx={{ fontSize: "2rem" }} />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            onClick={() => {
              if (!selectedRackId) {
                snackBar.showSnackbar("No Rack selected!", "error");
                return;
              }
              setOpenDrawer(true);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Rack</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              snackBar.showSnackbar("Disable will come soon", "info");
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Disable Rack</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose();
              handlePrintRack();
            }}
          >
            <ListItemIcon>
              <Print fontSize="small" />
            </ListItemIcon>
            <ListItemText>Print Rack</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {showPrintDialog && barcodeList.length > 0 && (
        <div style={{ display: "none" }}>
          <PrintableRackLabels ref={componentRef} barcodes={barcodeList} />
        </div>
      )}
    </AdminLayout>
  );
};
